import React from "react";
import { useSearchParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { base44 } from "../api/base44Client";
import { createPageUrl } from "../utils";
import { Clock, ArrowLeft, Calendar } from "lucide-react";
import { Button } from "../Components/ui/button";
import { Card } from "../Components/ui/card";
import { Badge } from "../Components/ui/badge";
import { Skeleton } from "../Components/ui/skeleton";
// import ReactMarkdown from "react-markdown";

const categoryColors = {
  "sun-safety": "bg-orange-100 text-orange-700 border-orange-200",
  "skin-health": "bg-coral-100 text-coral-700 border-coral-200",
  "technology": "bg-purple-100 text-purple-700 border-purple-200",
  "research": "bg-blue-100 text-blue-700 border-blue-200",
  "tips": "bg-green-100 text-green-700 border-green-200"
};

const categoryColorsDark = {
  "sun-safety": "bg-orange-500/20 text-orange-400 border-orange-500/30",
  "skin-health": "bg-coral-500/20 text-coral-400 border-coral-500/30",
  "technology": "bg-purple-500/20 text-purple-400 border-purple-500/30",
  "research": "bg-blue-500/20 text-blue-400 border-blue-500/30",
  "tips": "bg-green-500/20 text-green-400 border-green-500/30"
};

export default function Article() {
  const [searchParams] = useSearchParams();
  const slug = searchParams.get('slug');
  const [theme, setTheme] = React.useState(() => {
    return localStorage.getItem('theme') || 'dark';
  });

  React.useEffect(() => {
    const handleStorage = () => {
      setTheme(localStorage.getItem('theme') || 'dark');
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  const { data: article, isLoading } = useQuery({
    queryKey: ['article', slug],
    queryFn: () => base44.entities.Article.findBySlug(slug),
    enabled: !!slug
  });

  if (isLoading) {
    return (
      <div className={`min-h-screen ${
        theme === 'dark'
          ? 'bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900'
          : 'bg-gradient-to-br from-orange-50 via-white to-coral-50'
      }`}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <Skeleton className="h-12 w-32 mb-8" />
          <Skeleton className="h-16 w-full mb-4" />
          <Skeleton className="h-96 w-full mb-8" />
          <Skeleton className="h-4 w-full mb-2" />
          <Skeleton className="h-4 w-full mb-2" />
          <Skeleton className="h-4 w-3/4" />
        </div>
      </div>
    );
  }

  if (!article) {
    return (
      <div className={`min-h-screen ${
        theme === 'dark'
          ? 'bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900'
          : 'bg-gradient-to-br from-orange-50 via-white to-coral-50'
      }`}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 text-center">
          <h1 className={`text-3xl font-bold mb-4 ${
            theme === 'dark' ? 'text-white' : 'text-gray-900'
          }`}>Article not found</h1>
          <p className={`mb-8 ${
            theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
          }`}>The article you're looking for doesn't exist.</p>
          <Link to={createPageUrl('Articles')}>
            <Button>Back to Articles</Button>
          </Link>
        </div>
      </div>
    );
  }

  const displayDate = article.published_date || article.created_date;
  const categoryColor = theme === 'dark' 
    ? categoryColorsDark[article.category] || categoryColorsDark['tips']
    : categoryColors[article.category] || categoryColors['tips'];

  return (
    <div className={`min-h-screen ${
      theme === 'dark'
        ? 'bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900'
        : 'bg-gradient-to-br from-orange-50 via-white to-coral-50'
    }`}>
      {/* Header Image */}
      <div className="relative h-96 overflow-hidden">
        <img 
          src={article.cover_image || 'https://images.unsplash.com/photo-1559827260-dc66d52bef19?w=1600&h=900&fit=crop'} 
          alt={article.title}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 p-8 text-white">
          <div className="max-w-4xl mx-auto">
            <Badge className={`${categoryColor} mb-4`}>
              {article.category?.replace('-', ' ')}
            </Badge>
            <h1 className="text-4xl md:text-5xl font-bold mb-4">{article.title}</h1>
            <div className="flex items-center gap-6 text-white/80">
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                <span>{article.read_time || 5} min read</span>
              </div>
              {displayDate && (
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  <span>{new Date(displayDate).toLocaleDateString('en-US', { 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric' 
                  })}</span>
                </div>
              )}
              {article.author && (
                <div className="flex items-center gap-2">
                  <span className="text-white/90">By {article.author.name}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Back Button */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-8">
        <Link to={createPageUrl('Articles')}>
          <Button variant="ghost" className="mb-8">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Articles
          </Button>
        </Link>
      </div>

      {/* Article Content */}
      <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-20">
        <Card className={`p-8 md:p-12 ${
          theme === 'dark'
            ? 'bg-gray-800 border-gray-700'
            : 'bg-white border-gray-200'
        }`}>
          {article.author && (
            <div className={`mb-6 pb-6 border-b ${
              theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
            }`}>
              <p className={`font-semibold mb-1 ${
                theme === 'dark' ? 'text-white' : 'text-gray-900'
              }`}>
                {article.author.name}
              </p>
              <p className={`text-sm ${
                theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
              }`}>
                {article.author.role}
              </p>
            </div>
          )}
          {article.excerpt && (
            <p className={`text-xl mb-8 leading-relaxed ${
              theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
            }`}>
              {article.excerpt}
            </p>
          )}
          
          <div 
            className={`prose prose-lg max-w-none leading-relaxed ${
              theme === 'dark' 
                ? 'prose-invert prose-headings:text-white prose-p:text-gray-300 prose-a:text-orange-400 prose-strong:text-white'
                : 'prose-headings:text-gray-900 prose-p:text-gray-700 prose-a:text-orange-600'
            }`}
          >
            <div 
              className={`${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}
            >
              {article.content ? (
                article.content.split('\n').map((line, i) => {
                  if (!line.trim()) {
                    return <br key={i} />;
                  }
                  
                  // Check if line is a heading (# ## ###)
                  if (line.trim().startsWith('#')) {
                    const level = line.trim().match(/^#+/)?.[0].length || 1;
                    const text = line.trim().replace(/^#+\s*/, '');
                    const headingLevel = Math.min(level, 6);
                    
                    // Parse bold text in heading
                    const headingParts = [];
                    let currentIndex = 0;
                    const boldRegex = /\*\*(.*?)\*\*/g;
                    let match;
                    
                    while ((match = boldRegex.exec(text)) !== null) {
                      if (match.index > currentIndex) {
                        headingParts.push({ type: 'text', content: text.slice(currentIndex, match.index) });
                      }
                      headingParts.push({ type: 'bold', content: match[1] });
                      currentIndex = match.index + match[0].length;
                    }
                    if (currentIndex < text.length) {
                      headingParts.push({ type: 'text', content: text.slice(currentIndex) });
                    }
                    if (headingParts.length === 0) {
                      headingParts.push({ type: 'text', content: text });
                    }
                    
                    const headingClassName = `mb-4 mt-6 first:mt-0 font-bold ${
                      headingLevel === 1 ? 'text-3xl' : headingLevel === 2 ? 'text-2xl' : 'text-xl'
                    } ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`;
                    
                    const headingContent = headingParts.map((part, j) => 
                      part.type === 'bold' ? <strong key={j}>{part.content}</strong> : <span key={j}>{part.content}</span>
                    );
                    
                    if (headingLevel === 1) {
                      return <h1 key={i} className={headingClassName}>{headingContent}</h1>;
                    } else if (headingLevel === 2) {
                      return <h2 key={i} className={headingClassName}>{headingContent}</h2>;
                    } else if (headingLevel === 3) {
                      return <h3 key={i} className={headingClassName}>{headingContent}</h3>;
                    } else if (headingLevel === 4) {
                      return <h4 key={i} className={headingClassName}>{headingContent}</h4>;
                    } else if (headingLevel === 5) {
                      return <h5 key={i} className={headingClassName}>{headingContent}</h5>;
                    } else {
                      return <h6 key={i} className={headingClassName}>{headingContent}</h6>;
                    }
                  }
                  
                  // Regular paragraph with bold support
                  const parts = [];
                  let currentIndex = 0;
                  const boldRegex = /\*\*(.*?)\*\*/g;
                  let match;
                  
                  while ((match = boldRegex.exec(line)) !== null) {
                    if (match.index > currentIndex) {
                      parts.push({ type: 'text', content: line.slice(currentIndex, match.index) });
                    }
                    parts.push({ type: 'bold', content: match[1] });
                    currentIndex = match.index + match[0].length;
                  }
                  if (currentIndex < line.length) {
                    parts.push({ type: 'text', content: line.slice(currentIndex) });
                  }
                  if (parts.length === 0) {
                    parts.push({ type: 'text', content: line });
                  }
                  
                  return (
                    <p key={i} className="mb-4 last:mb-0">
                      {parts.map((part, j) => 
                        part.type === 'bold' ? <strong key={j}>{part.content}</strong> : <span key={j}>{part.content}</span>
                      )}
                    </p>
                  );
                })
              ) : (
                <p>{article.excerpt || 'Content coming soon...'}</p>
              )}
            </div>
          </div>
        </Card>
      </article>
    </div>
  );
}

